/**
 * @file: TimelineItem.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Plain timeline row: a thin vertical rail with a small dot per event,
 * content flows inline to the right (no card wrapper). The rail line is
 * drawn in two pieces (above the dot, below the dot) so the producer
 * can hide either side at the list boundaries (`isFirst` / `isLast`).
 *
 * Solid dot for fresh / unread, hollow ring for read so the timeline
 * stays scannable without colored backgrounds. Accent color follows the
 * category — gives each row a one-glance category cue without resorting
 * to icon chrome.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, ReactNode, memo } from 'react';
import { View } from 'react-native';

const kRailColor = '#d1d5db';
const kDotSizeRpx = 11;
const kRailWidthRpx = 28;
const kRailLineWidthRpx = 1;
/**
 * Vertical space the rail-line covers ABOVE the dot. Tuned so the dot's
 * center lines up with the first text line's center on the content side
 * (the category chip in the TimelineMessage header) — the chip text is
 * 11rpx with the surrounding header's small top-padding, so dot-center
 * lands at ~rpx(4) + rpx(5.5) ≈ rpx(9.5), matching the chip's vertical
 * center.
 */
const kTopRailHeightRpx = 5;

type Props = {
  accent: string;
  isFirst?: boolean;
  isLast?: boolean;
  /**
   * Hollow ring (read) vs solid dot (unread). Mirrors the IKEA-style
   * timeline: filled = current/fresh, hollow = past/acknowledged.
   */
  read?: boolean;
  children: ReactNode;
};

export const TimelineItem: FC<Props> = memo(
  ({ accent, isFirst, isLast, read, children }) => {
    const { rpx } = useResponsiveContext();
    const dotSize = rpx(kDotSizeRpx);
    const railWidth = rpx(kRailWidthRpx);
    const lineWidth = rpx(kRailLineWidthRpx);
    const topRail = rpx(kTopRailHeightRpx);
    // Floor the bottom rail so short messages still bridge the row gap
    // and the line never visually breaks.
    const bottomRailMin = rpx(12);
    return (
      <View
        css={`
          flex-direction: row;
        `}
      >
        <View
          css={`
            width: ${railWidth}px;
            align-items: center;
          `}
        >
          <View
            css={`
              width: ${lineWidth}px;
              height: ${topRail}px;
              background-color: ${isFirst ? 'transparent' : kRailColor};
            `}
          />
          {read ? (
            <View
              css={`
                width: ${dotSize}px;
                height: ${dotSize}px;
                border-radius: ${dotSize / 2}px;
                background-color: #fff;
                border-width: ${rpx(1.5)}px;
                border-color: ${accent};
              `}
            />
          ) : (
            <View
              css={`
                width: ${dotSize}px;
                height: ${dotSize}px;
                border-radius: ${dotSize / 2}px;
                background-color: ${accent};
              `}
            />
          )}
          <View
            css={`
              flex: 1;
              min-height: ${bottomRailMin}px;
              width: ${lineWidth}px;
              background-color: ${isLast ? 'transparent' : kRailColor};
            `}
          />
        </View>
        <View
          css={`
            flex: 1;
            padding-right: ${rpx(16)}px;
            padding-bottom: ${rpx(16)}px;
          `}
        >
          {children}
        </View>
      </View>
    );
  },
);
